import React from 'react';
import { Box, Stack } from '@mui/material';
import { Link } from 'react-router-dom';

import {
  Page,
  StatusChip,
  useAppLayoutContextConsumer,
  Button,
  EditOutlinedIcon,
  VisibilityOutlinedIcon,
  ArchiveOutlinedIcon,
  IPaginationProps,
} from '@occc-app/design-system';
import {
  AudienceBasicFragment,
  GeneralSurveyFragment,
  LocationFragment,
  Survey_Metrics_Period,
  SurveyLocationStatisticsFragment,
  SurveyMetricsFragment,
  SurveyStatus,
} from '@occc/graphql';
import { LoadedData } from '@occc-app/routes/Wallet/types';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { SurveyDetails } from './SurveyDetails';
import { SurveyMetricsSection } from './SurveyMetricsSection';
import { SurveyLocations } from './SurveyLocations';
import { SurveyStatisticsBarChartItem, SurveyStatisticsPieChartItem, DateRange } from './types';

import { getBreadcrumbLinks } from './utils/getBreadcrumbLinks';
import { chipVariantByStatus, titleByStatus } from '../constants';
import { routeCreator } from '../routeCreator';
import { SortingProps } from '../../../hooks/useSortingParams';

export type SurveyDetailsPage = {
  surveyData: LoadedData<GeneralSurveyFragment>;
  audienceData: LoadedData<AudienceBasicFragment>;
  locationStatisticsData: LoadedData<SurveyLocationStatisticsFragment[]>;
  responseStatisticsPerLocationMap: Record<
    string,
    SurveyStatisticsPieChartItem | SurveyStatisticsBarChartItem
  >;
  surveyMetricsData: LoadedData<SurveyMetricsFragment>;

  locationStatisticsPaginationProps: IPaginationProps;
  sortingProps?: SortingProps;
  onActivate: () => void;
  onArchive: () => void;
  allAnswersChartSectionProps: SurveyStatisticsPieChartItem | SurveyStatisticsBarChartItem | null;

  onPeriodChange: (period: Survey_Metrics_Period) => void;
  customDateRange: DateRange;
  metricsFilterMinSelectableDate?: Date;
  metricsFilterMaxSelectableDate?: Date;
  onCustomDateRangeChange: (value: DateRange) => void;
  metricsLocations: LocationFragment[];
  onMetricsLocationsChange: (value: LocationFragment[]) => void;
};

export const SurveyDetailsPage = ({
  surveyData,
  audienceData,
  locationStatisticsData,
  locationStatisticsPaginationProps,
  responseStatisticsPerLocationMap,
  surveyMetricsData,
  onActivate,
  onArchive,
  onPeriodChange,
  customDateRange,
  metricsFilterMinSelectableDate,
  metricsFilterMaxSelectableDate,
  onCustomDateRangeChange,
  metricsLocations,
  onMetricsLocationsChange,
  sortingProps,
  allAnswersChartSectionProps,
}: SurveyDetailsPage) => {
  const survey = surveyData.data;
  const surveyName = survey?.name;

  const isUserPermittedToEditSurvey = useIsUserPermittedTo(Feature.SURVEY_EDIT);
  const isUserPermittedToViewSurveyConfiguration = useIsUserPermittedTo(
    Feature.SURVEY_EDIT_VIEW_ONLY,
  );
  const isUserPermitterToArchiveSurvey = useIsUserPermittedTo(Feature.SURVEY_ARCHIVE);

  const links = React.useMemo(() => getBreadcrumbLinks(surveyName), [surveyName]);
  useAppLayoutContextConsumer(links);

  const isViewConfigurationButtonVisible =
    !isUserPermittedToEditSurvey && isUserPermittedToViewSurveyConfiguration;

  const actions = React.useMemo(() => {
    if (!survey) {
      return [];
    }

    if (isViewConfigurationButtonVisible) {
      return (
        <Button
          key='edit'
          color='secondary'
          variant='contained'
          component={Link}
          to={routeCreator.update(survey.uuid)}
          startIcon={<VisibilityOutlinedIcon />}
          role='button'
        >
          View Configuration
        </Button>
      );
    }

    const activateButton = (
      <Button key='activate' variant='contained' color='secondary' onClick={onActivate}>
        Activate
      </Button>
    );

    const archiveButton = isUserPermitterToArchiveSurvey && (
      <Button
        key='archive'
        startIcon={<ArchiveOutlinedIcon />}
        variant='outlined'
        onClick={onArchive}
        color='secondary'
      >
        Archive
      </Button>
    );

    const editButton = isUserPermittedToEditSurvey && (
      <Button
        key='edit'
        color='secondary'
        variant='contained'
        component={Link}
        to={routeCreator.update(survey.uuid)}
        startIcon={<EditOutlinedIcon />}
        role='button'
      >
        Edit
      </Button>
    );

    const actionItems: React.ReactNode[] = [];

    if (survey.status === SurveyStatus.Active) {
      actionItems.push(archiveButton, editButton);
    } else if (survey.status === SurveyStatus.Draft) {
      actionItems.push(archiveButton, activateButton, editButton);
    }

    return actionItems;
  }, [
    isUserPermittedToEditSurvey,
    isUserPermitterToArchiveSurvey,
    isViewConfigurationButtonVisible,
    onActivate,
    onArchive,
    survey,
  ]);

  return (
    <Page
      browserTabTitle='General Survey'
      actions={actions}
      pageHeaderProps={{
        title: survey?.name || 'General Survey',
        subtitle: surveyData.loading ? 'Loading...' : survey?.description,
        status: survey && (
          <Box mt={1}>
            <StatusChip
              label={titleByStatus[survey.status]}
              variant={chipVariantByStatus[survey.status]}
            />
          </Box>
        ),
      }}
      containerProps={{ variant: 'md' }}
    >
      <Stack spacing={1} pb={4}>
        <SurveyDetails
          surveyData={surveyData}
          audienceData={audienceData}
          allAnswersChartSectionProps={allAnswersChartSectionProps}
        />

        <SurveyMetricsSection
          customDateRangeMinSelectableDate={metricsFilterMinSelectableDate}
          customDateRangeMaxSelectableDate={metricsFilterMaxSelectableDate}
          surveyMetrics={surveyMetricsData}
          onPeriodChange={onPeriodChange}
          customDateRange={customDateRange}
          onCustomDateRangeChange={onCustomDateRangeChange}
          locations={metricsLocations}
          onLocationsChange={onMetricsLocationsChange}
        />

        <SurveyLocations
          locationStatisticsData={locationStatisticsData}
          responseStatisticsPerLocationMap={responseStatisticsPerLocationMap}
          paginationProps={locationStatisticsPaginationProps}
          sortingProps={sortingProps}
        />
      </Stack>
    </Page>
  );
};
